export type BecomeAMemberAction =
	| "opened"
	| "closed"
	| "plan_selected"
	| "checkout_started"
	| "checkout_blocked"
	| "checkout_confirmed"
	| "payment_failed"
	| "completed";

export type BecomeAMemberSurface = "modal" | "page" | "processing";

export type BecomeAMemberCloseReason = "x" | "not_interested" | "backdrop";

export type BecomeAMemberProvider = "stripe" | "paypal";

export type BecomeAMemberProperties = {
	surface: BecomeAMemberSurface;
	cta_click?: string | null;
	reason?: BecomeAMemberCloseReason;
	interval?: string;
	promo_code?: string | null;
	price_id?: string;
	error_code?: string;
	/** Stripe issuer decline reason when distinct from `error_code`. */
	decline_code?: string;
	error_message?: string;
	/** Stripe's own error when the card form fails to load. */
	stripe_error_type?: string;
	stripe_error_code?: string;
	stripe_error_message?: string;
	/** HTTP status from the checkout API when the failure came from our server. */
	http_status?: number;
	/** Distinguishes checkout failures from renewal/dunning (`membership:payment_failed`). */
	failure_source?: "checkout";
	provider?: BecomeAMemberProvider;
	subscription_status?: string;
	/** Amount shown for the selected price, in major currency units. */
	amount_due?: number;
	/** Dense-mobile experiment variant when captured from the modal. */
	density?: "control" | "test";
	layout?: "stacked_scroll" | "dense_stacked_scroll" | "side_by_side";
	/** Joining-price test arm and its one-time fee in dollars. */
	joining_price_variant?: string;
	joining_fee?: number;
};

/**
 * Builds the PostHog event name and sparse properties for membership-funnel
 * captures. Callers add `cta_click` from the CTA cookie when capturing.
 *
 * @param {BecomeAMemberAction} action - Funnel step
 * @param {BecomeAMemberProperties} properties - Step-specific properties
 * @returns {{ event: string; properties: Record<string, unknown> }} Capture payload
 */
export const buildBecomeAMemberEvent = (
	action: BecomeAMemberAction,
	properties: BecomeAMemberProperties,
): { event: string; properties: Record<string, unknown> } => ({
	event: `become_a_member:${action}`,
	properties: Object.fromEntries(
		Object.entries({
			...properties,
			...(action === "payment_failed"
				? { failure_source: properties.failure_source ?? "checkout" }
				: {}),
		}).filter(([, value]) => value != null),
	),
});
